import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';


/**
 * Detect whether the current device uses Cmd as its primary modifier
 * (macOS / iPadOS / iOS) vs Ctrl (Windows / Linux / ChromeOS / etc).
 * Returns the canonical label as a string so JSX can drop it inline.
 *
 * SSR-safe: starts with the conservative "Ctrl" default; flips to "Cmd"
 * after first render if the platform matches. The effect runs once.
 */
export function useMetaKeyLabel(): string {
  const [label, setLabel] = useState('Ctrl');
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const probe =
      // `userAgentData.platform` is the modern API (Chromium-based
      // browsers); `navigator.platform` is the legacy fallback.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigator as any).userAgentData?.platform ||
      navigator.platform ||
      navigator.userAgent ||
      '';
    const isMac = /Mac|iPhone|iPod|iPad/i.test(probe);
    setLabel(isMac ? 'Cmd' : 'Ctrl');
  }, []);
  return label;
}

/**
 * Same detection but returns the glyph form (⌘ for Mac, Ctrl for the
 * rest) — useful inside compact UI like keyboard hint chips.
 */
export function useMetaKeyGlyph(): string {
  const [glyph, setGlyph] = useState('Ctrl');
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const probe =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigator as any).userAgentData?.platform ||
      navigator.platform ||
      navigator.userAgent ||
      '';
    const isMac = /Mac|iPhone|iPod|iPad/i.test(probe);
    setGlyph(isMac ? '⌘' : 'Ctrl');
  }, []);
  return glyph;
}

/**
 * Vim-style g-prefix navigation shortcuts.  Press `g`, then a second key,
 * within 1.5 seconds.  Single-key shortcuts (`?`) fire immediately.
 *
 * The hook is registered once at the Layout level.  It deliberately ignores
 * key events while the user is typing into an input/textarea/contenteditable
 * surface, so editing forms doesn't accidentally trigger navigation.
 */
export interface KeyboardShortcut {
  /** Visible label in the help dialog ("Go to home"). */
  label: string;
  /** Full key sequence ("g h"). */
  combo: string;
  /** What to do when the combo fires. */
  action: () => void;
  /** Group heading in the help dialog. */
  group: string;
}

interface UseKeyboardShortcutsOptions {
  /** Toggle help dialog open. Wired by Layout to its own state. */
  onOpenHelp: () => void;
  onCloseHelp: () => void;
  isHelpOpen: boolean;
}

export function useNavigationShortcuts({
  onOpenHelp,
  onCloseHelp,
  isHelpOpen,
}: UseKeyboardShortcutsOptions): KeyboardShortcut[] {
  const navigate = useNavigate();

  // Build a stable list of shortcuts for the help dialog and the hotkey
  // dispatcher.  Ordered by group so the help dialog renders neatly.
  const shortcuts: KeyboardShortcut[] = [
    { group: 'Navigation', combo: 'g h', label: 'Go to home', action: () => navigate('/dashboard/') },
    { group: 'Navigation', combo: 'g c', label: 'Go to candidates', action: () => navigate('/dashboard/candidates') },
    { group: 'Navigation', combo: 'g n', label: 'Invite new candidate', action: () => navigate('/dashboard/candidates/new') },
    { group: 'Navigation', combo: 'g r', label: 'Go to score queue', action: () => navigate('/dashboard/reviews') },
    { group: 'Navigation', combo: 'g p', label: 'Go to passport queue', action: () => navigate('/dashboard/reviews/passports') },
    { group: 'Navigation', combo: 'g f', label: 'Go to flag queues', action: () => navigate('/dashboard/flag-queue') },
    { group: 'Navigation', combo: 'g i', label: 'Browse item bank', action: () => navigate('/dashboard/items') },
    { group: 'Navigation', combo: 'g a', label: 'Open audit log', action: () => navigate('/dashboard/audit') },
    { group: 'Help', combo: '?', label: 'Show keyboard shortcuts', action: () => onOpenHelp() },
  ];

  // Pending g-prefix.  Reset after 1.5s of inactivity.
  const pendingRef = useRef<{ key: string; expires: number } | null>(null);

  useEffect(() => {
    function isTypingTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (target.isContentEditable) return true;
      return false;
    }

    function onKeyDown(e: KeyboardEvent) {
      // Modifier keys cancel — leave Cmd-K, Ctrl-S etc. alone.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Don't intercept while focus is inside a form field.
      if (isTypingTarget(e.target)) return;

      // Help dialog open: only Escape closes it.
      if (isHelpOpen) {
        if (e.key === 'Escape') {
          e.preventDefault();
          onCloseHelp();
        }
        return;
      }

      // Single-key shortcuts.
      if (e.key === '?') {
        e.preventDefault();
        onOpenHelp();
        return;
      }

      // 'g' starts a multi-key sequence.
      if (e.key === 'g' && !pendingRef.current) {
        pendingRef.current = { key: 'g', expires: Date.now() + 1500 };
        return;
      }

      // Continue an active g-sequence.
      if (pendingRef.current && Date.now() < pendingRef.current.expires) {
        const combo = `${pendingRef.current.key} ${e.key}`.toLowerCase();
        const match = shortcuts.find((s) => s.combo === combo);
        pendingRef.current = null;
        if (match) {
          e.preventDefault();
          match.action();
        }
        return;
      }

      // Stale prefix — discard.
      pendingRef.current = null;
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [shortcuts, isHelpOpen, onOpenHelp, onCloseHelp]);

  return shortcuts;
}
